import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiFileText,
  FiRefreshCw,
  FiTrash2,
} from "react-icons/fi";

import { PageContainer } from "../../components/PageComponents";
import { useAppSelector } from "../../hooks/hooks";

interface LogEvent {
  id: string;
  timestamp: string;
  environment: string;
  level: string;
  message: string;
  raw: string;
  line_count: number;
}

interface LogResponse {
  file: string;
  events: LogEvent[];
  event_count: number;
  requested_events: number;
  size_bytes: number;
  modified_at: string | null;
  exists: boolean;
  truncated: boolean;
}

const eventOptions = [25, 50, 100, 250, 500];

const formatBytes = (bytes: number) => {
  if (bytes === 0) return "0 B";

  const units = ["B", "KB", "MB", "GB"];
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );

  return `${(bytes / 1024 ** unitIndex).toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const levelClasses = (level: string) => {
  if (["EMERGENCY", "ALERT", "CRITICAL", "ERROR"].includes(level)) {
    return "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300";
  }

  if (level === "WARNING") {
    return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300";
  }

  if (["NOTICE", "INFO"].includes(level)) {
    return "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300";
  }

  return "border-gray-200 bg-gray-50 text-gray-600 dark:border-white/10 dark:bg-white/5 dark:text-gray-300";
};

const formatDateHeading = (date: string) => {
  const parsedDate = new Date(`${date}T00:00:00`);

  return Number.isNaN(parsedDate.getTime())
    ? date
    : parsedDate.toLocaleDateString(undefined, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
};

const errorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message || fallback;
  }

  return fallback;
};

const ApplicationLogs = () => {
  const { token, user } = useAppSelector((state) => state.auth);
  const [log, setLog] = useState<LogResponse | null>(null);
  const [eventLimit, setEventLimit] = useState(100);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const canManageLogs = ["super_admin", "admin", "developer"].includes(
    String(user?.role ?? ""),
  );

  const loadLog = useCallback(async () => {
    if (!token) {
      setError("Your admin session has expired. Please sign in again.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get<LogResponse>(
        `${process.env.REACT_APP_API_URL}/admin/logs`,
        {
          params: { events: eventLimit },
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setLog(response.data);
    } catch (requestError: unknown) {
      setError(
        errorMessage(requestError, "The Laravel log could not be loaded."),
      );
    } finally {
      setLoading(false);
    }
  }, [eventLimit, token]);

  useEffect(() => {
    loadLog();
  }, [loadLog]);

  const groupedEvents = useMemo(() => {
    const groups = new Map<string, LogEvent[]>();

    (log?.events ?? []).forEach((event) => {
      const date = event.timestamp.slice(0, 10);
      groups.set(date, [...(groups.get(date) ?? []), event]);
    });

    return Array.from(groups.entries());
  }, [log?.events]);

  const handleDeleteEvent = async (event: LogEvent) => {
    const confirmed = window.confirm(
      `Delete the ${event.level} event from ${event.timestamp}? This cannot be undone.`,
    );

    if (!confirmed || !token) return;

    setDeletingEventId(event.id);
    setActionMessage(null);
    setError(null);

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/admin/logs/events/${event.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setActionMessage("Log event deleted.");
      await loadLog();
    } catch (requestError: unknown) {
      setError(errorMessage(requestError, "The log event could not be deleted."));
    } finally {
      setDeletingEventId(null);
    }
  };

  const handleClearLogs = async () => {
    const confirmed = window.confirm(
      "Clear every Laravel log event, including rotated daily logs? This cannot be undone.",
    );

    if (!confirmed || !token) return;

    setClearing(true);
    setActionMessage(null);
    setError(null);

    try {
      const response = await axios.delete<{ message: string }>(
        `${process.env.REACT_APP_API_URL}/admin/logs`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setActionMessage(response.data.message);
      await loadLog();
    } catch (requestError: unknown) {
      setError(errorMessage(requestError, "The Laravel logs could not be cleared."));
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="w-full pb-6">
      <PageContainer title="Application Logs">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-500 dark:text-gray-300">
              <span className="inline-flex items-center gap-2 font-medium text-gray-700 dark:text-white">
                <FiFileText aria-hidden="true" />
                {log?.file ?? "laravel.log"}
              </span>
              <span>{formatBytes(log?.size_bytes ?? 0)}</span>
              <span>{log?.event_count ?? 0} events loaded</span>
              <span>
                Modified: {" "}
                {log?.modified_at
                  ? new Date(log.modified_at).toLocaleString()
                  : "Not available"}
              </span>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                Show
                <select
                  value={eventLimit}
                  onChange={(event) => setEventLimit(Number(event.target.value))}
                  className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-gray-800 outline-none focus:border-pry-600 dark:border-white/20 dark:bg-white/10 dark:text-white"
                  aria-label="Number of log events"
                >
                  {eventOptions.map((option) => (
                    <option key={option} value={option} className="text-black">
                      {option} events
                    </option>
                  ))}
                </select>
              </label>

              {canManageLogs && (
                <button
                  type="button"
                  onClick={handleClearLogs}
                  disabled={clearing || loading || !log?.exists}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-red-300 px-4 font-medium text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/40"
                >
                  <FiTrash2 aria-hidden="true" />
                  {clearing ? "Clearing…" : "Clear all logs"}
                </button>
              )}

              <button
                type="button"
                onClick={loadLog}
                disabled={loading}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#027EC2] to-[#039EF2] px-4 font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FiRefreshCw
                  aria-hidden="true"
                  className={loading ? "animate-spin" : ""}
                />
                Refresh
              </button>
            </div>
          </div>

          {error && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300"
            >
              <FiAlertCircle className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          {actionMessage && (
            <div
              role="status"
              className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700 dark:border-green-900/60 dark:bg-green-950/30 dark:text-green-300"
            >
              <FiCheckCircle className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>{actionMessage}</span>
            </div>
          )}

          <div aria-busy={loading} className="flex min-h-[420px] flex-col gap-6">
            {loading && !log ? (
              <div className="flex min-h-[420px] items-center justify-center rounded-lg border border-gray-200 bg-white text-sm text-gray-400 dark:border-white/10 dark:bg-white/5">
                Loading Laravel log events…
              </div>
            ) : groupedEvents.length > 0 ? (
              groupedEvents.map(([date, events]) => (
                <section key={date} className="flex flex-col gap-3">
                  <h2 className="sticky top-[76px] z-10 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm dark:border-white/10 dark:bg-[#202024] dark:text-gray-200">
                    {formatDateHeading(date)}
                  </h2>

                  {events.map((event) => (
                    <article
                      key={event.id}
                      className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-white/5"
                    >
                      <div className="flex flex-col gap-3 border-b border-gray-100 p-4 dark:border-white/10 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <time className="font-mono text-sm font-semibold text-gray-800 dark:text-white">
                              {event.timestamp}
                            </time>
                            <span
                              className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${levelClasses(event.level)}`}
                            >
                              {event.level}
                            </span>
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-white/10 dark:text-gray-300">
                              {event.environment}
                            </span>
                            <span className="text-xs text-gray-400">
                              {event.line_count} {event.line_count === 1 ? "line" : "lines"}
                            </span>
                          </div>
                          <p className="break-words text-sm text-gray-700 dark:text-gray-200">
                            {event.message || "No event message"}
                          </p>
                        </div>

                        {canManageLogs && (
                          <button
                            type="button"
                            onClick={() => handleDeleteEvent(event)}
                            disabled={deletingEventId === event.id || clearing}
                            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-sm text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/40"
                            aria-label={`Delete log event from ${event.timestamp}`}
                          >
                            <FiTrash2 aria-hidden="true" />
                            {deletingEventId === event.id ? "Deleting…" : "Delete"}
                          </button>
                        )}
                      </div>

                      <details className="group">
                        <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-pry-600 hover:bg-gray-50 dark:hover:bg-white/5">
                          View complete event
                        </summary>
                        <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words border-t border-gray-800 bg-[#0d1117] p-4 font-mono text-xs leading-5 text-gray-200 sm:text-sm">
                          {event.raw}
                        </pre>
                      </details>
                    </article>
                  ))}
                </section>
              ))
            ) : (
              <div className="flex min-h-[420px] items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500 dark:border-white/20 dark:bg-white/5 dark:text-gray-400">
                {log?.exists
                  ? "The Laravel log contains no timestamped events."
                  : "No Laravel log file exists yet."}
              </div>
            )}
          </div>

          {log?.truncated && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Showing the newest {log.event_count} timestamped events. Choose a
              larger event count to load more history.
            </p>
          )}
        </div>
      </PageContainer>
    </div>
  );
};

export default ApplicationLogs;
